// Đọc file master thông số MSI (sheet "NB", khóa "Part No") và dựng "Thông số chuẩn MSI".
// Port từ dự án MSI Product Specs (src/lib/master/parse.ts, src/lib/mapping/engine.ts,
// src/data/displaySchema.ts, src/data/columnGroups.ts) để hai web đọc master và hiển thị giống nhau.

// ── Chuẩn hóa giá trị ──────────────────────────────────────
const EMPTY_MARKERS = /^(n\/?a|-+|—)$/i;
const STRAY_QUOTES = /^[“”"]+|[“”"]+$/g;

// Cắt khoảng trắng, bỏ ngoặc kép lạc, coi N/A / NA / - là trống
export function normalizeValue(raw) {
  if (raw === null || raw === undefined) return null;
  let v = String(raw).replace(/\r\n?/g, "\n").replace(/ /g, " ").trim();
  if (/^[“”"].*[“”"]$/s.test(v)) v = v.replace(STRAY_QUOTES, "").trim();
  if (!v || EMPTY_MARKERS.test(v)) return null;
  // Danh sách có phần tử giữ chỗ, ví dụ "fTPM 2.0, Kensington Lock, NA"
  if (v.includes(",")) {
    const items = v.split(/\s*,\s*/);
    if (items.some(i => EMPTY_MARKERS.test(i))) {
      v = items.filter(i => i && !EMPTY_MARKERS.test(i)).join(", ");
      if (!v) return null;
    }
  }
  return v;
}

export const normalizeHeader = (raw) => String(raw ?? "").replace(/\s+/g, " ").trim();

// Giá trị ô của exceljs → chuỗi
function cellText(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "object") {
    if (v.richText) return v.richText.map(r => r.text).join("");
    if (v.text !== undefined) return String(v.text);
    if (v.result !== undefined) return v.result === null ? null : String(v.result);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (v.error) return null;
    return null;
  }
  return String(v);
}

/**
 * Đọc file master .xlsx → { sheetName, columns: [key], products: [{ partNo, values }], warnings }.
 * Cột trùng tên được đặt key "Tên (2)", "Tên (3)"… như web Specs.
 */
export async function parseMasterFile(arrayBuffer, { keyColumn = "Part No" } = {}) {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(arrayBuffer);
  const ws = wb.getWorksheet("NB") || wb.worksheets[0];
  if (!ws) throw new Error("File không có sheet nào");
  const warnings = [];

  const headerRow = ws.getRow(1);
  const seen = new Map();
  const columns = [];   // { key, index }
  for (let c = 1; c <= ws.columnCount; c++) {
    const header = normalizeHeader(cellText(headerRow.getCell(c).value));
    if (!header) continue;
    const n = (seen.get(header) || 0) + 1;
    seen.set(header, n);
    const key = n === 1 ? header : `${header} (${n})`;
    if (n > 1) warnings.push(`Cột "${header}" bị trùng ở cột ${c}; lưu thành "${key}"`);
    columns.push({ key, index: c });
  }
  const keyCol = columns.find(c => c.key === keyColumn);
  if (!keyCol) throw new Error(`Không thấy cột "${keyColumn}" trong sheet "${ws.name}"`);

  const byPartNo = new Map();
  for (let r = 2; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    const values = {};
    let any = false;
    for (const c of columns) {
      const v = normalizeValue(cellText(row.getCell(c.index).value));
      if (v !== null) { values[c.key] = v; any = true; }
    }
    if (!any) continue;
    const partNo = values[keyColumn];
    if (!partNo) { warnings.push(`Dòng ${r} không có ${keyColumn}; bỏ qua`); continue; }
    if (byPartNo.has(partNo)) warnings.push(`${keyColumn} ${partNo} bị trùng ở dòng ${r}; lấy dòng sau`);
    byPartNo.set(partNo, { partNo, values });
  }
  return { sheetName: ws.name, columns: columns.map(c => c.key), products: [...byPartNo.values()], warnings };
}

// ── Engine nhỏ: nguồn + các bước xử lý (giống web Specs) ──
const PORT_COUNT = /^\d+\s*x$/i;
const HAS_COUNT_PREFIX = /^\d+\s*x\s+\S/i;
const UNITS = { MB: 1 / 1024, GB: 1, TB: 1024 };

/** "8GB*2" → "16GB"; "1TB*1" → "1TB"; chữ khác giữ nguyên */
export function capacityTotal(v) {
  const m = /^\s*(\d+(?:\.\d+)?)\s*(MB|GB|TB)\s*(?:[*x×]\s*(\d+))?\s*$/i.exec(v || "");
  if (!m) return v;
  const gb = Number(m[1]) * UNITS[m[2].toUpperCase()] * Number(m[3] ?? 1);
  if (gb >= 1024 && gb % 1024 === 0) return `${gb / 1024}TB`;
  return `${Number(gb.toPrecision(12))}GB`;
}

function renderTemplate(template, lookup) {
  const fill = (text, strict) => {
    let missing = false;
    const out = text.replace(/\{([^{}]+)\}/g, (_, name) => {
      const v = lookup(name.trim());
      if (v === null) missing = true;
      return v ?? "";
    });
    return strict && missing ? null : out;
  };
  const withGroups = template.replace(/\[\[([\s\S]*?)\]\]/g, (_, inner) => fill(inner, true) ?? "");
  const out = fill(withGroups, false).replace(/[ \t]{2,}/g, " ").trim();
  return out === "" ? null : out;
}

function evaluateSource(source, values, columns) {
  const get = (col) => values[col] ?? null;
  switch (source.type) {
    case "column": return get(source.column);
    case "template":
      return renderTemplate(source.template, (name) => {
        for (const alt of name.split("|")) {
          const v = get(alt.trim());
          if (v !== null) return v;
        }
        return null;
      });
    case "ports": {
      const i = columns.indexOf(source.from), j = columns.indexOf(source.to);
      const cols = [...(i >= 0 && j >= i ? columns.slice(i, j + 1) : []), ...(source.include || [])];
      const items = [];
      for (const col of cols) {
        const v = get(col);
        if (v === null) continue;
        const name = col.replace(/ \(\d+\)$/, "");
        if (PORT_COUNT.test(v)) items.push(`${v.replace(/\s+/g, "")} ${name}`);
        else if (HAS_COUNT_PREFIX.test(v)) items.push(v);
        else items.push(`${v} ${name}`);
      }
      return items.length ? items.join(source.separator ?? " / ") : null;
    }
    default: return null;
  }
}

function applyStep(value, step) {
  if (value === null) return null;
  switch (step.op) {
    case "replace": {
      const out = value.replace(new RegExp(step.pattern, step.flags ?? "gi"), step.with).trim();
      return out === "" ? null : out;
    }
    case "extract": {
      const m = new RegExp(step.pattern, step.flags ?? "i").exec(value);
      const g = m?.[step.group ?? 1] ?? m?.[0];
      return g ? g.trim() : null;
    }
    case "capacityTotal": return capacityTotal(value);
    default: return value;
  }
}

// ── Khung "Thông số chuẩn MSI" (giống trang sản phẩm của web Specs, theo template-MSI.pdf) ──
const NL = "\n";
const col = (column, ...steps) => ({ source: { type: "column", column }, steps });
const tpl = (template, ...steps) => ({ source: { type: "template", template }, steps });
const replace = (pattern, w) => ({ op: "replace", pattern, with: w });
const extract = (pattern, group = 1) => ({ op: "extract", pattern, group });
const ONE_LINE = replace("\\s*\\n\\s*", ", ");
const CPU_WEB = "CPU Number_Website For APJ / India|CPU Number_Website";
const PORT_RANGE = { type: "ports", from: "Type-C (USB3.1 Gen2 / DP / Thunderbolt™3)", to: "Mini Display Port", include: ["RJ45"] };

const row = (id, vi, en, spec, lineBreak) => ({ id, label: { vi, en }, ...spec, lineBreak });

export const MSI_SPEC_ROWS = [
  row("os", "Hệ điều hành", "Operating system", col("OS_Website")),
  row("display", "Màn hình", "Display", tpl("{Screen Size_Website} {Pixel_Website}, {Frame Rate}[[, {Color Range_Website|Color Range}]][[, {Peak Brightness (HDR Value)}]], {Display Type_Website}[[, {LCD Features}]]")),
  row("cpu", "CPU", "Processor", tpl(`{${CPU_WEB}}`)),
  row("gpu", "Card đồ họa", "Graphics",
    tpl(`[[{GPU1 MKT Name_Website}${NL}]][[{GPU1 Boost Clock_Website}${NL}]][[{Combined Power_Website}${NL}]]{GPU1 Graphics_Website}`,
      replace("\\s*\\n\\s*\\*May vary by scenario", " *May vary by scenario")), NL),
  row("ram", "RAM", "Memory",
    tpl("{Memory Size} {Memory Module|Memory Max Speed}[[ {Memory Max Speed}]][[, {Memory Slot}]][[, tối đa {Memory Max Capacity_Website}]]",
      replace("Max\\s*", ""), replace("^(\\d+)\\s*(GB)\\*(\\d+)", "$3x$1$2"))),
  row("storage", "Ổ cứng", "Storage", tpl("{SSD Size} {SSD Interface}[[ + {SSD 2 Size} {SSD 2 Interface}]]", replace("\\*1(?=\\s|$)", ""))),
  row("m2", "Số khe M.2", "M.2 slots", col("SSD (NVMe PCIe Gen)_Website"), ", "),
  row("security", "Bảo mật", "Security", col("Security Type_Website", ONE_LINE), ", "),
  row("network", "Kết nối mạng", "Network", tpl("{WLAN/BT_Website}[[ {LAN_Website}]]", replace(",\\s*Bluetooth[^,]*", ""))),
  row("bluetooth", "Bluetooth", "Bluetooth", col("WLAN/BT_Website", extract("(Bluetooth\\s*v?[\\d.]+)"))),
  row("ports", "Cổng kết nối", "Ports", { source: { ...PORT_RANGE, separator: NL } }, NL),
  row("keyboard", "Bàn phím", "Keyboard", col("Keyboard Types_Website")),
  row("touchpad", "Touch Pad", "Touchpad", col("Touchpad")),
  row("audioJack", "Jack âm thanh", "Audio jack", col("Audio Jack_Website")),
  row("speakers", "Loa", "Speakers", tpl(`{Audio Speaker} Speaker[[${NL}{Audio Woofer} Woofer]][[${NL}{Audio Solution_Website}]]`), NL),
  row("webcam", "Webcam", "Webcam", col("WebCam")),
  row("battery", "Pin", "Battery", tpl("{Battery Cell_Website} / {Whrs}")),
  row("adapter", "Sạc", "Adapter", col("Adaptor")),
  row("weight", "Trọng lượng", "Weight", col("Weight")),
  row("dimension", "Kích thước", "Dimensions", col("Dimension")),
  row("warranty", "Bảo hành", "Warranty", col("Warranty_Website")),
  row("color", "Màu sắc", "Color", col("Color_Website")),
];

/** Thông số chuẩn MSI của một SKU: [{ id, label, lines }] (bỏ các dòng trống) */
export function msiStandardSpecs(values = {}, columns = []) {
  return MSI_SPEC_ROWS.map(r => {
    let v = evaluateSource(r.source, values, columns);
    for (const s of r.steps || []) v = applyStep(v, s);
    const split = (s) => (r.lineBreak ? s.split(r.lineBreak) : [s]);
    const lines = v ? v.split("\n").flatMap(split).map(s => s.trim()).filter(Boolean) : [];
    return { id: r.id, label: r.label, lines };
  }).filter(r => r.lines.length);
}

// ── Nhóm cột của master (cho phần "Tất cả thông số") ──
export const COLUMN_GROUPS = [
  { id: "identity", label: { vi: "Nhận diện & phân loại", en: "Identity & category" }, start: "Part No" },
  { id: "color", label: { vi: "Màu sắc & vỏ máy", en: "Color & chassis" }, start: "Color" },
  { id: "display", label: { vi: "Màn hình", en: "Display" }, start: "Screen Size" },
  { id: "cpu", label: { vi: "CPU", en: "CPU" }, start: "CPU Type" },
  { id: "gpu", label: { vi: "Card đồ họa & tản nhiệt", en: "Graphics & cooling" }, start: "GPU1 Vendor" },
  { id: "memory", label: { vi: "RAM", en: "Memory" }, start: "Memory Size" },
  { id: "storage", label: { vi: "Ổ cứng", en: "Storage" }, start: "SSD Form Factor" },
  { id: "network", label: { vi: "Mạng LAN / Wi-Fi", en: "LAN / Wi-Fi" }, start: "LAN_Website" },
  { id: "ports", label: { vi: "Cổng kết nối", en: "Ports" }, start: "Type-C (USB3.1 Gen2 / DP / Thunderbolt™3)" },
  { id: "input", label: { vi: "Bàn phím, touchpad, webcam", en: "Keyboard, touchpad, webcam" }, start: "Keyboard Types_Website" },
  { id: "audio", label: { vi: "Âm thanh", en: "Audio" }, start: "Audio Jack_Website" },
  { id: "power", label: { vi: "Sạc & pin", en: "Power & battery" }, start: "Adaptor" },
  { id: "os", label: { vi: "Hệ điều hành", en: "Operating system" }, start: "OS" },
  { id: "size", label: { vi: "Kích thước & trọng lượng", en: "Size & weight" }, start: "Dimension" },
  { id: "security", label: { vi: "Bảo mật, chứng nhận, cảm biến", en: "Security, certification, sensors" }, start: "Security Type_Website" },
  { id: "warranty", label: { vi: "Bảo hành, phụ kiện, phần mềm", en: "Warranty, accessories, software" }, start: "Warranty" },
  { id: "extra", label: { vi: "Mã & thông tin bổ sung", en: "Codes & extra info" }, start: "Segment Name2 (2)" },
];

/** Gom các giá trị có dữ liệu theo nhóm cột, giữ thứ tự cột của master */
export function groupedSpecs(values = {}, columns = []) {
  const startAt = new Map();
  COLUMN_GROUPS.forEach(g => { const i = columns.indexOf(g.start); if (i >= 0) startAt.set(i, g.id); });
  let current = COLUMN_GROUPS[0].id;
  const groups = new Map(COLUMN_GROUPS.map(g => [g.id, { ...g, items: [] }]));
  const order = columns.length ? columns : Object.keys(values);
  order.forEach((c, i) => {
    current = startAt.get(i) ?? current;
    if (values[c] !== undefined) groups.get(current).items.push({ column: c, value: values[c] });
  });
  return [...groups.values()].filter(g => g.items.length);
}

// ── Trường gọn cho bảng SKU (theo "Cấu hình trường") ──
// single: cột đầu tiên có dữ liệu; concat: ghép các cột có dữ liệu
const lookupCI = (values, column) => {
  if (values[column] !== undefined) return values[column];
  const lc = column.trim().toLowerCase();
  const k = Object.keys(values).find(x => x.toLowerCase() === lc);
  return k ? values[k] : null;
};
export function mapFields(values, fields) {
  const out = {};
  for (const f of fields) {
    const parts = (f.columns || []).map(c => lookupCI(values, c)).filter(Boolean);
    out[f.key] = f.type === "concat" ? parts.join(" ") : (parts[0] || "");
  }
  return out;
}

export const HIGHEND_GPU = /rtx\s*(4080|4090|5070 ti|5080|5090)|quadro|rtx 6000/i;

export const specDocId = (partNo) => String(partNo).replace(/\//g, "_");
